import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, MapPin, ExternalLink } from "lucide-react";
import { TESTIMONIALS, REVIEW_LOCATIONS } from "@/data/site";

const initialsOf = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const ReviewCard = ({ t, i }) => (
  <motion.figure
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
    data-testid={`testimonial-${i}`}
    className="flex h-full min-h-[300px] flex-col rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-7 transition-transform duration-300 hover:-translate-y-1 hover:shadow-pop"
  >
    <div className="flex items-start justify-between">
      <div className="flex gap-1 text-koreyaah-red">
        {Array.from({ length: t.rating }).map((_, s) => (
          <Star key={s} size={16} fill="currentColor" />
        ))}
      </div>
      <Quote size={32} className="text-koreyaah-red/15" fill="currentColor" />
    </div>
    <blockquote className="mt-5 flex-1 leading-relaxed text-koreyaah-indigo/80 line-clamp-5">“{t.text}”</blockquote>
    <figcaption className="mt-6 flex items-center gap-3 border-t border-koreyaah-indigo/10 pt-5">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-koreyaah-indigo text-sm font-bold text-white">{initialsOf(t.name)}</span>
      <span className="min-w-0">
        <span className="block truncate font-display text-base font-semibold text-koreyaah-indigo">{t.name}</span>
        <span className="flex items-center gap-1 text-xs text-koreyaah-indigo/50"><MapPin size={11} /> {t.location}</span>
      </span>
    </figcaption>
  </motion.figure>
);

export const ReviewsTabs = () => {
  const [active, setActive] = useState(REVIEW_LOCATIONS[0].key);
  const loc = REVIEW_LOCATIONS.find((l) => l.key === active);
  const reviews = TESTIMONIALS.filter((t) => t.location === active);

  return (
    <div className="mt-10">
      {/* Segmented control */}
      <div className="inline-flex rounded-full border-2 border-koreyaah-indigo/15 bg-white p-1.5" data-testid="reviews-tabs" role="tablist">
        {REVIEW_LOCATIONS.map((l) => {
          const isActive = active === l.key;
          return (
            <button
              key={l.key}
              role="tab"
              aria-selected={isActive}
              data-testid={`reviews-tab-${l.key.toLowerCase()}`}
              onClick={() => setActive(l.key)}
              className="relative rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors duration-200"
            >
              {isActive && (
                <motion.span
                  layoutId="review-tab-pill"
                  className="absolute inset-0 rounded-full bg-koreyaah-red"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )}
              <span className={`relative z-10 flex items-center gap-1.5 ${isActive ? "text-white" : "text-koreyaah-indigo"}`}>
                <MapPin size={14} /> {l.label}
              </span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.3 }}
          className="mt-8"
        >
          {/* Google rating summary */}
          <div data-testid="reviews-summary" className="flex flex-col gap-6 rounded-[2rem] bg-koreyaah-indigo p-8 text-koreyaah-cream sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-5">
              <span className="font-display text-5xl font-bold leading-none">{loc.rating}</span>
              <div>
                <div className="flex gap-1 text-[#FBBC04]">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={18} fill="currentColor" className={s < Math.round(loc.rating) ? "" : "opacity-30"} />
                  ))}
                </div>
                <p className="mt-2 text-sm text-koreyaah-cream/70">
                  Rated on Google · Koreyaah {loc.label}
                  {loc.count ? ` · ${loc.count}+ reviews` : ""}
                </p>
              </div>
            </div>
            <a
              href={loc.url}
              target="_blank"
              rel="noreferrer"
              data-testid="reviews-google-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-koreyaah-red px-7 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-koreyaah-red-deep"
            >
              View All Reviews on Google <ExternalLink size={16} />
            </a>
          </div>

          {/* Reviews grid */}
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((t, i) => (
              <ReviewCard key={t.name} t={t} i={i} />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
